import GeoJSON from 'geojson'
import mapboxgl, { ExpressionSpecification, GeoJSONSourceSpecification, SymbolLayerSpecification } from 'mapbox-gl'

import { hasLiveStyle, safeAddLayer, safeAddSource } from '../MapView/mapTeardown'
import { navaidIcons } from './icons'

export const NAVAID_SOURCE_ID = 'navaids'
export const MIN_NAVAID_ZOOM = 7
export const MIN_NAVAID_LABEL_ZOOM = 8

// Os PNGs padrão de navaid têm 144px na origem, contra ~204px do aeródromo e ~108-120px do
// obstáculo. Reduzimos o multiplicador para mantê-los visualmente proporcionais às demais camadas.
export const NAVAID_ICON_SIZE: ExpressionSpecification = ['interpolate', ['linear'], ['zoom'], 7, 0.1875, 11, 0.2825]

export const NAVAID_LAYER_IDS = {
  icons: 'map-navaids-icons-layer',
  selectedIcons: 'map-navaids-selected-icons-layer',
  labels: 'map-navaids-labels-layer'
} as const

export const NAVAID_LAYER_ORDER = [
  NAVAID_LAYER_IDS.icons,
  NAVAID_LAYER_IDS.selectedIcons,
  NAVAID_LAYER_IDS.labels
] as const

const emptySelectedNavaidId = ''

const hasSelection = (selectedNavaidId: string): boolean => selectedNavaidId !== emptySelectedNavaidId

export const navaidFilters = {
  icons: (selectedNavaidId = emptySelectedNavaidId): ExpressionSpecification => ['!=', ['get', 'id'], selectedNavaidId],
  selectedIcons: (selectedNavaidId = emptySelectedNavaidId): ExpressionSpecification => [
    '==',
    ['get', 'id'],
    selectedNavaidId
  ],
  labels: (selectedNavaidId = emptySelectedNavaidId): ExpressionSpecification =>
    hasSelection(selectedNavaidId) ? ['==', ['get', 'id'], selectedNavaidId] : ['has', 'id']
}

export function createNavaidSourceSpecification(
  data: GeoJSON.FeatureCollection<GeoJSON.Point>
): GeoJSONSourceSpecification {
  return {
    type: 'geojson',
    data,
    cluster: false
  }
}

export function createNavaidLayerSpecifications(selectedNavaidId?: string): SymbolLayerSpecification[] {
  return [
    {
      id: NAVAID_LAYER_IDS.icons,
      type: 'symbol',
      source: NAVAID_SOURCE_ID,
      minzoom: MIN_NAVAID_ZOOM,
      filter: navaidFilters.icons(selectedNavaidId),
      layout: {
        'icon-image': ['get', 'iconName'],
        'icon-size': NAVAID_ICON_SIZE,
        'icon-anchor': 'center',
        'icon-padding': 0,
        'icon-allow-overlap': true,
        'icon-ignore-placement': true,
        'icon-optional': false,
        'symbol-z-order': 'source',
        'symbol-sort-key': 1
      }
    },
    {
      id: NAVAID_LAYER_IDS.selectedIcons,
      type: 'symbol',
      source: NAVAID_SOURCE_ID,
      minzoom: 0,
      filter: navaidFilters.selectedIcons(selectedNavaidId),
      layout: {
        'icon-image': ['get', 'iconName'],
        'icon-size': NAVAID_ICON_SIZE,
        'icon-anchor': 'center',
        'icon-padding': 0,
        'icon-allow-overlap': true,
        'icon-ignore-placement': true,
        'icon-optional': false,
        'symbol-z-order': 'source',
        'symbol-sort-key': 2
      }
    },
    {
      id: NAVAID_LAYER_IDS.labels,
      type: 'symbol',
      source: NAVAID_SOURCE_ID,
      minzoom: MIN_NAVAID_LABEL_ZOOM,
      filter: navaidFilters.labels(selectedNavaidId),
      layout: {
        'text-field': ['get', 'label'],
        'text-font': ['Open Sans Bold'],
        'text-size': 12,
        'text-anchor': 'top',
        'text-offset': [0, 1.2],
        'text-justify': 'center',
        'text-keep-upright': true,
        // Colisão ligada apenas nas labels: quando duas se sobrepõem, só a de maior
        // prioridade (menor sort-key) é exibida. Os ícones continuam todos visíveis.
        'text-allow-overlap': false,
        'text-ignore-placement': false,
        'symbol-z-order': 'source',
        'symbol-sort-key': ['coalesce', ['get', 'sortKey'], 99]
      },
      paint: {
        'text-color': '#fcfcfc',
        'text-halo-color': '#060809',
        'text-halo-width': 1.5,
        'text-halo-blur': 0.25
      }
    }
  ]
}

export function addNavaidSourceAndLayers(
  map: mapboxgl.Map,
  data: GeoJSON.FeatureCollection<GeoJSON.Point>,
  beforeLayerId?: string,
  selectedNavaidId?: string
): void {
  safeAddSource(map, NAVAID_SOURCE_ID, createNavaidSourceSpecification(data))

  createNavaidLayerSpecifications(selectedNavaidId).forEach((layer) => {
    safeAddLayer(map, layer, beforeLayerId)
  })
}

export function removeNavaidLayersAndSource(map: mapboxgl.Map): void {
  const layerRemovalOrder = [...NAVAID_LAYER_ORDER].reverse()

  layerRemovalOrder.forEach((layerId) => {
    if (map.getLayer(layerId)) {
      map.removeLayer(layerId)
    }
  })

  if (map.getSource(NAVAID_SOURCE_ID)) {
    map.removeSource(NAVAID_SOURCE_ID)
  }
}

export function setNavaidSelectionFilter(map: mapboxgl.Map, selectedNavaidId?: string): void {
  if (!hasLiveStyle(map)) return

  if (map.getLayer(NAVAID_LAYER_IDS.icons)) {
    map.setFilter(NAVAID_LAYER_IDS.icons, navaidFilters.icons(selectedNavaidId))
  }

  if (map.getLayer(NAVAID_LAYER_IDS.selectedIcons)) {
    map.setFilter(NAVAID_LAYER_IDS.selectedIcons, navaidFilters.selectedIcons(selectedNavaidId))
  }

  if (map.getLayer(NAVAID_LAYER_IDS.labels)) {
    map.setFilter(NAVAID_LAYER_IDS.labels, navaidFilters.labels(selectedNavaidId))
  }
}

export function setNavaidSourceData(map: mapboxgl.Map, data: GeoJSON.FeatureCollection<GeoJSON.Point>): void {
  if (!hasLiveStyle(map)) return

  const source = map.getSource(NAVAID_SOURCE_ID) as mapboxgl.GeoJSONSource | undefined
  if (source) {
    source.setData(data)
  }
}

export async function registerNavaidImages(map: mapboxgl.Map): Promise<void> {
  await Promise.all(
    Object.entries(navaidIcons).map(([imageId, imageSrc]) => {
      return new Promise<void>((resolve, reject) => {
        if (map.hasImage(imageId)) {
          resolve()
          return
        }

        map.loadImage(imageSrc, (error, image) => {
          if (error) {
            reject(error)
            return
          }

          if (!image) {
            reject(new Error(`Could not load navaid image ${imageId}`))
            return
          }

          // The style may have been reloaded while the image was loading.
          if (hasLiveStyle(map) && !map.hasImage(imageId)) {
            map.addImage(imageId, image)
          }

          resolve()
        })
      })
    })
  )
}
